import React, { useContext, useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Divider,
  Box,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import WebIcon from "@material-ui/icons/Web";
import StarOutlineIcon from "@material-ui/icons/StarOutline";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "../../utils/toast";
import { getPublicUrl } from "../../utils/runtimeUrls";

import logoDefault from "../../assets/logo.png";
import logoWhiteDefault from "../../assets/logo_white.png";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";

  return {
    root: {
      width: "100%",
      padding: theme.spacing(3),
    },
    headerBox: {
      marginBottom: theme.spacing(3),
    },
    title: {
      fontSize: 22,
      fontWeight: 600,
      color: dark ? "#e9edef" : "#111b21",
    },
    subtitle: {
      fontSize: 14,
      color: dark ? "#8696a0" : "#54656f",
      marginTop: 4,
    },
    card: {
      backgroundColor: dark ? "#202c33" : "#fff",
      borderRadius: 14,
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      boxShadow: dark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 2px 12px rgba(0,0,0,0.06)",
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "hidden",
      transition: "transform 0.15s, box-shadow 0.15s",
      "&:hover": {
        boxShadow: dark ? "0 6px 24px rgba(0,0,0,0.6)" : "0 4px 20px rgba(0,0,0,0.12)",
      },
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "16px 20px",
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
    },
    cardTitle: {
      fontWeight: 600,
      fontSize: 16,
      color: dark ? "#e9edef" : "#111b21",
    },
    previewAreaLight: {
      backgroundColor: "#f0f2f5",
      minHeight: 140,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    },
    previewAreaDark: {
      backgroundColor: "#0b141a",
      minHeight: 140,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    },
    logoImg: {
      maxHeight: 80,
      maxWidth: "85%",
      objectFit: "contain",
    },
    cardBody: {
      padding: "16px 20px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      flex: 1,
    },
    cardDescription: {
      fontSize: 13,
      color: dark ? "#8696a0" : "#54656f",
      marginBottom: 16,
    },
    fileInput: {
      display: "none",
    },
    uploadBtn: {
      borderRadius: 8,
      textTransform: "none",
      fontWeight: 600,
    },
    saveBtn: {
      borderRadius: 8,
      textTransform: "none",
      fontWeight: 600,
      backgroundColor: "#00a884",
      color: "#fff",
      "&:hover": {
        backgroundColor: "#008f6f",
      },
      marginTop: 8,
    },
    selectedFileInfo: {
      fontSize: 12,
      color: dark ? "#00a884" : "#008069",
      fontWeight: 500,
      marginTop: 8,
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
  };
});

const LogoItemCard = ({
  title,
  description,
  refKey,
  icon: Icon,
  darkPreview,
  fallbackAsset,
  serverPath,
  cacheBuster,
  onUploaded,
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const initialServerUrl = `${getPublicUrl(serverPath)}?r=${cacheBuster}`;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/png", "image/x-icon", "image/svg+xml", "image/jpeg", "image/webp"];
    if (!allowed.includes(file.type) && !file.name.endsWith(".ico")) {
      toast.error("Formato inválido! Use arquivos PNG, SVG, WEBP ou ICO.");
      return;
    }

    setSelectedFile(file);
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await api.post(`/settings/media-upload?ref=${refKey}`, formData);
      if (response.data?.mensagem === "Arquivo Anexado" || response.status === 200) {
        toast.success(`Logotipo (${title}) atualizado com sucesso!`);
        setSelectedFile(null);
        if (onUploaded) onUploaded();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <Icon style={{ color: darkPreview ? "#00a884" : "#ff5722", fontSize: 22 }} />
        <Typography className={classes.cardTitle}>{title}</Typography>
      </div>

      <div className={darkPreview ? classes.previewAreaDark : classes.previewAreaLight}>
        <img
          className={classes.logoImg}
          src={previewUrl || initialServerUrl}
          onError={(e) => {
            if (fallbackAsset) {
              e.target.onerror = null;
              e.target.src = fallbackAsset;
            }
          }}
          alt={title}
        />
      </div>

      <div className={classes.cardBody}>
        <Typography className={classes.cardDescription}>{description}</Typography>

        <input
          type="file"
          ref={fileInputRef}
          className={classes.fileInput}
          onChange={handleFileChange}
          accept="image/png,image/svg+xml,image/x-icon,image/webp,image/jpeg"
        />

        <Button
          variant="outlined"
          color="primary"
          className={classes.uploadBtn}
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
        >
          {selectedFile ? "Trocar Arquivo" : "Selecionar Imagem"}
        </Button>

        {selectedFile && (
          <Box mt={1}>
            <div className={classes.selectedFileInfo}>
              <CheckCircleOutlineIcon style={{ fontSize: 16 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {selectedFile.name}
              </span>
            </div>

            <Button
              variant="contained"
              fullWidth
              className={classes.saveBtn}
              onClick={handleUpload}
              disabled={loading}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Salvar Alteração"}
            </Button>
          </Box>
        )}
      </div>
    </div>
  );
};

const Uploader = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const [cacheBuster, setCacheBuster] = useState(Date.now());

  useEffect(() => {
    if (!user.super && user.profile !== "admin") {
      toast.error("Sem permissão para acessar!");
      history.push("/");
    }
  }, [user, history]);

  const refreshCache = () => {
    setCacheBuster(Date.now());
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerBox}>
        <Typography className={classes.title}>Personalização Visual e Logotipos</Typography>
        <Typography className={classes.subtitle}>
          Altere aqui as imagens e logotipos da sua plataforma para os temas claro e escuro. As alterações refletem em todo o sistema.
        </Typography>
      </div>

      <Grid container spacing={3}>
        {/* Logo Tema Claro */}
        <Grid item xs={12} sm={6} md={6}>
          <LogoItemCard
            title="Logotipo Tema Claro"
            description="Exibido na tela de login, atendimentos em aberto e telas com fundo claro. Recomenda-se formato PNG com fundo transparente."
            refKey="login"
            serverPath="public/logotipos/login.png"
            fallbackAsset={logoDefault}
            icon={Brightness7Icon}
            darkPreview={false}
            cacheBuster={cacheBuster}
            onUploaded={refreshCache}
          />
        </Grid>

        {/* Logo Tema Escuro (Branco) */}
        <Grid item xs={12} sm={6} md={6}>
          <LogoItemCard
            title="Logotipo Tema Escuro (Branco)"
            description="Exibido quando o usuário ativa o Modo Escuro (Dark Mode). Deve possuir tipografia branca ou clara com fundo transparente."
            refKey="login_white"
            serverPath="public/logotipos/login_white.png"
            fallbackAsset={logoWhiteDefault}
            icon={Brightness4Icon}
            darkPreview={true}
            cacheBuster={cacheBuster}
            onUploaded={refreshCache}
          />
        </Grid>

        {/* Logotipo Interno / Topbar */}
        <Grid item xs={12} sm={6} md={6}>
          <LogoItemCard
            title="Logotipo Interno / Painel"
            description="Utilizado no cabeçalho interno da aplicação e menus colapsados."
            refKey="interno"
            serverPath="public/logotipos/interno.png"
            fallbackAsset={logoDefault}
            icon={WebIcon}
            darkPreview={false}
            cacheBuster={cacheBuster}
            onUploaded={refreshCache}
          />
        </Grid>

        {/* Favicon do Navegador */}
        <Grid item xs={12} sm={6} md={6}>
          <LogoItemCard
            title="Favicon / Ícone da Aba"
            description="Ícone exibido na aba do navegador e atalhos favoritos. Recomenda-se imagem quadrada (PNG ou ICO 256x256)."
            refKey="favicon"
            serverPath="public/logotipos/favicon.ico"
            fallbackAsset={logoDefault}
            icon={StarOutlineIcon}
            darkPreview={false}
            cacheBuster={cacheBuster}
            onUploaded={refreshCache}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default Uploader;